import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DatePickerModule } from 'primeng/datepicker';
import { FileUploadModule } from 'primeng/fileupload';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { ContentService } from '../../../../shared/services/content/content.service';
import { DEFAULT_LANGUAGE_CODE } from '../../../../shared/util/language-codes';
import { applyZodErrors } from '../../../../shared/util/zod-form-errors';
import {
  Content,
  CONTENT_TYPES,
  StoreContentSchema,
} from '../../../../shared/models/content.model';

@Component({
  selector: 'app-content-detail',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    CardModule,
    ButtonModule,
    DatePickerModule,
    FileUploadModule,
    InputTextModule,
    InputNumberModule,
    MessageModule,
    SelectModule,
    TextareaModule,
    ToastModule,
    ConfirmDialogModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './content-detail.component.html',
  styleUrl: './content-detail.component.scss',
})
export class ContentDetailComponent implements OnInit {
  service = inject(ContentService);
  #messageService = inject(MessageService);
  #confirmationService = inject(ConfirmationService);
  #translate = inject(TranslateService);
  #route = inject(ActivatedRoute);
  #router = inject(Router);
  #fb = inject(FormBuilder);

  model = signal<Content | null>(null);
  isEditMode = signal(false);
  loading = signal(false);
  submitting = signal(false);

  image = computed(() => this.model()?.image ?? null);

  typeOptions = CONTENT_TYPES.map((type) => ({
    value: type,
    label: `content.announcements.types.${type}`,
  }));

  form: FormGroup = this.#fb.group({
    type: ['info_banner' as string, [Validators.required]],
    rank: [0 as number, [Validators.required, Validators.min(0)]],
    valid_from: [null as Date | null],
    valid_until: [null as Date | null],
    headline: ['', [Validators.maxLength(255)]],
    text: [''],
    cta_text: ['', [Validators.maxLength(255)]],
    cta_link: ['', [Validators.maxLength(255)]],
  });

  ngOnInit(): void {
    const idParam = this.#route.snapshot.paramMap.get('id');
    if (idParam && idParam !== 'new') {
      this.isEditMode.set(true);
      this.#loadModel(Number(idParam));
    }
  }

  #loadModel(id: number) {
    this.loading.set(true);
    this.service.getById(id).subscribe({
      next: (response) => {
        this.loading.set(false);
        const item = response.data;
        this.model.set(item);
        this.form.patchValue({
          type: item.type,
          rank: item.rank,
          valid_from: item.valid_from ? new Date(item.valid_from) : null,
          valid_until: item.valid_until ? new Date(item.valid_until) : null,
          headline: item.headline ?? '',
          text: item.text ?? '',
          cta_text: item.cta_text ?? '',
          cta_link: item.cta_link ?? '',
        });
      },
      error: (e: { status?: number }) => {
        this.loading.set(false);
        if (e?.status === 404) {
          this.#notifyError('content.announcements.notFound');
          this.#router.navigate(['/content/announcements']);
        } else {
          this.#notifyError('content.announcements.loadError');
        }
      },
    });
  }

  onSubmit() {
    if (this.submitting()) return;
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    const parsed = StoreContentSchema.safeParse({
      type: raw.type,
      rank: Number(raw.rank ?? 0),
      valid_from: this.#toIso(raw.valid_from),
      valid_until: this.#toIso(raw.valid_until),
      details: [
        {
          language: DEFAULT_LANGUAGE_CODE,
          headline: raw.headline || null,
          text: raw.text || null,
          cta_text: raw.cta_text || null,
          cta_link: raw.cta_link || null,
        },
      ],
    });

    if (!parsed.success) {
      // The schema names the text row `details.0.*`; the form keeps that
      // single default-language row flat.
      applyZodErrors(parsed.error, this.form, {
        'details.0.headline': 'headline',
        'details.0.text': 'text',
        'details.0.cta_text': 'cta_text',
        'details.0.cta_link': 'cta_link',
      });
      return;
    }

    this.submitting.set(true);
    const editing = this.model();
    const call$ = editing
      ? this.service.update(editing.id, parsed.data)
      : this.service.create(parsed.data);

    call$.subscribe({
      next: (response) => {
        this.submitting.set(false);
        this.model.set(response.data);
        this.#messageService.add({
          severity: 'success',
          summary: this.#translate.instant('label.successHeader'),
          detail: this.#translate.instant(
            editing
              ? 'content.announcements.updated'
              : 'content.announcements.created',
          ),
        });
        if (!editing) {
          this.isEditMode.set(true);
          this.#router.navigate(['/content/announcements', response.data.id]);
        }
      },
      error: (e: unknown) => {
        this.submitting.set(false);
        this.#applyServerErrors(e);
        this.#notifyError(
          editing
            ? 'content.announcements.updateError'
            : 'content.announcements.createError',
        );
      },
    });
  }

  onImageSelected(event: { files: File[] }) {
    const item = this.model();
    const file = event.files?.[0];
    if (!item || !file) return;

    this.service.uploadImage(item.id, file).subscribe({
      next: () => {
        this.#messageService.add({
          severity: 'success',
          summary: this.#translate.instant('label.successHeader'),
          detail: this.#translate.instant(
            'content.announcements.imageUploaded',
          ),
        });
        this.#loadModel(item.id);
      },
      error: () => this.#notifyError('content.announcements.imageUploadError'),
    });
  }

  onImageRemove() {
    const item = this.model();
    const media = this.image();
    if (!item || !media) return;

    this.service.deleteImage(item.id, media.id).subscribe({
      next: () => {
        this.#messageService.add({
          severity: 'success',
          summary: this.#translate.instant('label.successHeader'),
          detail: this.#translate.instant('content.announcements.imageRemoved'),
        });
        this.#loadModel(item.id);
      },
      error: () => this.#notifyError('content.announcements.imageRemoveError'),
    });
  }

  onDelete(event: Event) {
    const item = this.model();
    if (!item) return;

    this.#confirmationService.confirm({
      target: event.target as EventTarget,
      header: this.#translate.instant(
        'content.announcements.deleteConfirmHeader',
      ),
      message: this.#translate.instant(
        'content.announcements.deleteConfirmMessage',
      ),
      acceptLabel: this.#translate.instant('label.delete'),
      rejectLabel: this.#translate.instant('label.cancel'),
      acceptButtonProps: { severity: 'danger' },
      accept: () => {
        this.service.delete(item.id).subscribe({
          next: () => {
            this.#messageService.add({
              severity: 'success',
              summary: this.#translate.instant('label.successHeader'),
              detail: this.#translate.instant('content.announcements.deleted'),
            });
            this.#router.navigate(['/content/announcements']);
          },
          error: () => this.#notifyError('content.announcements.deleteError'),
        });
      },
    });
  }

  onCancel() {
    this.#router.navigate(['/content/announcements']);
  }

  /**
   * The API stores UTC; the picker hands back a local Date. Sending the
   * ISO string keeps the offset explicit so the validity window means the
   * same moment on both sides.
   */
  #toIso(value: Date | null): string | null {
    return value ? value.toISOString() : null;
  }

  #notifyError(key: string) {
    this.#messageService.add({
      severity: 'error',
      summary: this.#translate.instant('label.errorHeader'),
      detail: this.#translate.instant(key),
    });
  }

  #applyServerErrors(error: unknown) {
    const errBag = (error as { error?: { errors?: Record<string, string[]> } })
      ?.error?.errors;
    if (!errBag) return;

    // The API reports the text row as `details.0.<field>`; the form keeps
    // the single default-language row flat.
    for (const key of Object.keys(errBag)) {
      const control =
        this.form.get(key) ?? this.form.get(key.replace('details.0.', ''));
      if (control) {
        control.setErrors({ server: { message: errBag[key][0] } });
        control.markAsTouched();
      }
    }
  }
}
